import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Shield, User, Lock, RefreshCw, Palette } from 'lucide-react';
import toast from 'react-hot-toast';
import { authApi } from '../../services/api';
import { useTheme } from '../../hooks/useTheme';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';

// 粒子背景组件
function ParticleBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    let animationId: number;
    let particles: Array<{
      x: number;
      y: number;
      vx: number;
      vy: number;
      size: number;
      opacity: number;
    }> = [];
    
    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    
    const createParticles = () => {
      particles = [];
      const count = Math.min(80, Math.floor((canvas.width * canvas.height) / 15000));
      for (let i = 0; i < count; i++) {
        particles.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          vx: (Math.random() - 0.5) * 0.5,
          vy: (Math.random() - 0.5) * 0.5,
          size: Math.random() * 2 + 1,
          opacity: Math.random() * 0.5 + 0.2
        });
      }
    };
    
    const drawParticles = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      particles.forEach((p, i) => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(56, 189, 248, ${p.opacity})`;
        ctx.fill();
        
        p.x += p.vx;
        p.y += p.vy;
        
        if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;
        
        particles.slice(i + 1).forEach(p2 => {
          const dx = p.x - p2.x;
          const dy = p.y - p2.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          
          if (dist < 150) {
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.strokeStyle = `rgba(56, 189, 248, ${0.15 * (1 - dist / 150)})`;
            ctx.lineWidth = 1;
            ctx.stroke();
          }
        });
      });
      
      animationId = requestAnimationFrame(drawParticles);
    };
    
    resize();
    createParticles();
    drawParticles();
    
    window.addEventListener('resize', () => {
      resize();
      createParticles();
    });
    
    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', resize);
    };
  }, []);
  
  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 pointer-events-none"
      style={{ background: 'transparent' }}
    />
  );
}

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, isLoading, isAuthenticated } = useAuthStore();
  
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [captcha, setCaptcha] = useState('');
  const [captchaData, setCaptchaData] = useState<{
    captchaId: string;
    imageBase64: string;
  } | null>(null);
  const [rememberMe, setRememberMe] = useState(false);
  const { theme, setTheme } = useTheme();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const fetchCaptcha = async () => {
    try {
      const data = await authApi.getCaptcha();
      setCaptchaData({
        captchaId: data.captchaId,
        imageBase64: data.imageBase64,
      });
    } catch (error) {
      toast.error('验证码加载失败');
    }
  };

  useEffect(() => {
    fetchCaptcha();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!username || !password) {
      toast.error('请输入用户名和密码');
      return;
    }
    
    if (!captcha) {
      toast.error('请输入验证码');
      return;
    }
    
    if (!captchaData) {
      toast.error('验证码加载失败，请刷新重试');
      return;
    }
    
    try {
      await login(username, password, captcha, captchaData.captchaId);
      toast.success('登录成功');
      navigate('/', { replace: true });
    } catch (error: any) {
      toast.error(error.message || '登录失败');
      fetchCaptcha();
      setCaptcha('');
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-background flex items-center justify-center p-6">
      {/* 背景渐变 */}
      <div className="absolute inset-0 bg-gradient-to-br from-background via-muted/50 to-primary/10" />
      
      {/* 发光效果 */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-3xl animate-pulse" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
      
      {/* 粒子背景 */}
      <ParticleBackground />
      
      {/* 主登录卡片 */}
      <div className="relative z-10 w-full max-w-xl">
        {/* 卡片外发光 */}
        <div className="absolute -inset-1 bg-gradient-to-r from-primary via-blue-500 to-indigo-500 rounded-2xl blur opacity-30 animate-pulse" />
        
        <div className="relative rounded-3xl p-10 bg-card/90 backdrop-blur-xl border border-primary/30 shadow-2xl shadow-primary/15">

          {/* 主题切换 */}
          <div className="absolute top-4 right-4 w-28">
            <Select value={theme} onValueChange={(v) => setTheme(v as any)}>
              <SelectTrigger className="h-8 text-xs bg-muted/50">
                <div className="flex items-center gap-1.5">
                  <Palette className="w-3.5 h-3.5 text-muted-foreground" />
                  <SelectValue placeholder="主题" />
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="light">浅色办公</SelectItem>
                <SelectItem value="light-contrast">高对比浅色</SelectItem>
                <SelectItem value="dark-tech">深色科技</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Logo 区域 */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center mb-5">
              <div className="relative">
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-cyan-500 to-blue-600 blur-lg opacity-40 animate-pulse" />
                <div className="relative w-20 h-20 rounded-2xl bg-gradient-to-br from-muted to-background border-2 border-primary/40 flex items-center justify-center shadow-2xl shadow-primary/30">
                  <div className="text-center">
                    <span className="text-xl font-black tracking-tighter bg-gradient-to-b from-foreground to-muted-foreground bg-clip-text text-transparent">
                      UCAS
                    </span>
                    <div className="text-[6px] text-primary tracking-widest mt-0.5 font-semibold">
                      中国科学院大学
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <h1 className="text-3xl font-bold bg-gradient-to-r from-primary via-blue-500 to-indigo-500 bg-clip-text text-transparent tracking-tight">
              国科大官微数据报表
            </h1>
            <p className="text-muted-foreground mt-2 text-sm">UCAS WeChat Official Account Analytics Platform</p>

            {/* 学校元素装饰条 */}
            <div className="mt-4 flex items-center justify-center gap-2">
              <div className="h-px w-12 bg-gradient-to-r from-transparent to-cyan-500/50" />
              <span className="text-xs text-primary/80 font-medium tracking-widest">University of Chinese Academy of Sciences</span>
              <div className="h-px w-12 bg-gradient-to-l from-transparent to-cyan-500/50" />
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* 用户名 */}
            <div className="space-y-2">
              <Label htmlFor="username" className="text-foreground">用户名</Label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                  <User className="w-5 h-5" />
                </div>
                <Input
                  id="username"
                  type="text"
                  placeholder="请输入用户名"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="pl-10 h-12 bg-muted border-border text-foreground placeholder:text-muted-foreground focus:border-primary rounded-xl text-base"
                />
              </div>
            </div>

            {/* 密码 */}
            <div className="space-y-2">
              <Label htmlFor="password" className="text-foreground">密码</Label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                  <Lock className="w-5 h-5" />
                </div>
                <Input
                  id="password"
                  type="password"
                  placeholder="请输入密码"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 h-12 bg-muted border-border text-foreground placeholder:text-muted-foreground focus:border-primary rounded-xl text-base"
                />
              </div>
            </div>

            {/* 验证码 */}
            <div className="space-y-2">
              <Label htmlFor="captcha" className="text-foreground">验证码</Label>
              <div className="flex gap-3">
                <div className="relative flex-1">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                    <Shield className="w-5 h-5" />
                  </div>
                  <Input
                    id="captcha"
                    type="text"
                    placeholder="请输入验证码"
                    value={captcha}
                    onChange={(e) => setCaptcha(e.target.value)}
                    maxLength={4}
                    className="pl-10 h-12 bg-muted border-border text-foreground placeholder:text-muted-foreground focus:border-primary rounded-xl tracking-widest text-center font-mono text-lg"
                  />
                </div>
                <button
                  type="button"
                  onClick={fetchCaptcha}
                  className="flex-shrink-0 w-28 h-12 rounded-xl border border-border overflow-hidden bg-muted hover:border-primary/50 transition-all"
                >
                  {captchaData?.imageBase64 ? (
                    <img
                      src={captchaData.imageBase64.startsWith('data:') 
                        ? captchaData.imageBase64 
                        : `data:image/png;base64,${captchaData.imageBase64}`}
                      alt="验证码"
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <RefreshCw className="w-5 h-5 text-muted-foreground animate-spin" />
                    </div>
                  )}
                </button>
              </div>
            </div>

            {/* 记住我 */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="remember"
                  checked={rememberMe}
                  onCheckedChange={(checked) => setRememberMe(checked as boolean)}
                  className="border-border data-[state=checked]:bg-primary"
                />
                <Label htmlFor="remember" className="text-sm text-muted-foreground cursor-pointer">
                  记住我
                </Label>
              </div>
            </div>

            {/* 登录按钮 */}
            <Button
              type="submit"
              disabled={isLoading}
              className="w-full h-12 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-semibold rounded-xl shadow-lg shadow-cyan-500/25 text-base"
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  登录中...
                </span>
              ) : (
                '登 录'
              )}
            </Button>
          </form>

          {/* 底部信息 */}
          <div className="mt-8 pt-5 border-t border-border text-center">
            <span className="text-sm text-muted-foreground">中国科学院大学 · 党委宣传部</span>
            <div className="mt-1 text-xs text-muted-foreground/70">Official Account Data Report System © 2026</div>
          </div>
        </div>
      </div>
    </div>
  );
}
